import React, { useRef, useState } from "react";
import { MapPin, Phone, Mail } from "lucide-react";
import Footer from "./Footer";
import { useLanguage } from "../LanguageContext";
import { translations } from "../translations";

const ContactSection = () => {
  const { language } = useLanguage();
  const formRef = useRef(null); // Reference to the form element
  const [status, setStatus] = useState(""); // State for success/error messages
  const [isLoading, setIsLoading] = useState(false); // State for loading status
  const [isFormValid, setIsFormValid] = useState(false); // State to track if form is valid

  // Handle input change to check if form is valid
  const handleInputChange = () => {
    const formData = new FormData(formRef.current);
    let isValid = true;

    // Check if required fields are filled (name, email, contact_no, message are required)
    if (
      !formData.get("name") ||
      !formData.get("email") ||
      !formData.get("contact_no") ||
      !formData.get("message")
    ) {
      isValid = false;
    }

    setIsFormValid(isValid);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (!isFormValid) {
      setStatus(translations[language]["FillAllFields"]);
      return;
    }

    setIsLoading(true); // Set loading state to true
    setStatus(""); // Clear any previous status

    try {
      const formData = new FormData(formRef.current);
      const response = await fetch("https://formspree.io/f/mjkwgjpq", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setStatus(translations[language].MessageSentSuccessfully);
        formRef.current.reset();
        setIsFormValid(false);
        setTimeout(() => setStatus(""), 5000);
      } else {
        throw new Error("Form submission failed");
      }
    } catch (error) {
      console.error(error);
      setStatus(translations[language].FailedToSendMessage);
      setTimeout(() => setStatus(""), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Main Content */}
      <div className="flex-grow bg-white pt-12 pb-12 max-w-6xl items-center mx-auto">
        <section className="container mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Side - Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-red-700">
                {translations[language].GetInTouch}
              </h2>
              <div className="mt-6 space-y-6">
                {/* Visit Us */}
                <div className="flex items-center p-6 bg-white shadow-lg rounded-lg">
                  <MapPin className="text-red-700 w-10 h-10 mr-4" />
                  <div>
                    <h3 className="text-lg text-gray-900 font-semibold">
                      {translations[language].visitUs}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {translations[language].MCBWIAddress}
                    </p>
                  </div>
                </div>

                {/* Call Us */}
                <div className="flex items-center p-6 bg-white shadow-lg rounded-lg">
                  <Phone className="text-red-700 w-10 h-10 mr-4" />
                  <div>
                    <h3 className="text-lg text-gray-900 font-semibold">
                      {translations[language].callUs}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {translations[language].Ph1} <br />{" "}
                      {translations[language].Ph2}
                    </p>
                  </div>
                </div>

                {/* Email Us */}
                <div className="flex items-center p-6 bg-white shadow-lg rounded-lg">
                  <Mail className="text-red-700 w-10 h-10 mr-4" />
                  <div>
                    <h3 className="text-lg text-gray-900 font-semibold">
                      {translations[language].emailUs}
                    </h3>
                    <p className="text-gray-600 text-sm">contact@mcbwi.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-red-700">
                {translations[language].SendUsAnEnquiry}
              </h2>
              <form
                ref={formRef} // Attach ref to the form
                onSubmit={handleSubmit} // Handle submission
                action="https://formspree.io/f/{your-form-id}" // Formspree endpoint
                method="POST"
                className="mt-6 space-y-4"
                onChange={handleInputChange} // Add onChange to check form validity
              >
                <input
                  type="text"
                  name="name" // Add name attribute
                  placeholder={translations[language].FullName}
                  className="w-full p-3 border border-gray-300 rounded-md text-black"
                  required
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    name="email" // Add name attribute
                    placeholder={translations[language].email}
                    className="w-full p-3 border border-gray-300 rounded-md text-black"
                    required
                  />
                  <input
                    type="text"
                    name="contact_no" // Add name attribute
                    placeholder={translations[language].contactNo}
                    className="w-full p-3 border border-gray-300 rounded-md text-black"
                    required
                  />
                </div>
                <input
                  type="text"
                  name="company" // Add name attribute
                  placeholder={translations[language].company}
                  className="w-full p-3 border border-gray-300 rounded-md text-black"
                />
                <textarea
                  name="message" // Add name attribute
                  rows="4"
                  placeholder={translations[language].message}
                  className="w-full p-3 border border-gray-300 rounded-md text-black"
                  required
                ></textarea>
                <button
                  type="submit"
                  className={`w-full py-3 rounded-md font-semibold transition ${
                    isLoading
                      ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                      : !isFormValid
                      ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                  disabled={isLoading || !isFormValid} // Disable button when loading or form is invalid
                >
                  {isLoading
                    ? "Loading..."
                    : translations[language].sendMessage}
                </button>
              </form>
              {/* Display status message */}
              {status && (
                <p className="mt-4 text-center text-gray-600">{status}</p>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Footer Sticks at the Bottom */}
      <Footer />
    </div>
  );
};

export default ContactSection;